import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import LoginHub from './LoginHub';
import SupasoLogoSpinner from './SupasoLogoSpinner';
import './SuperAdminHub.css';

const DELEGACIONES_MAP = {
    "Buenos Aires": ["Avellaneda (PBA)", "La Plata (PBA)"],
    "CABA": ["CABA"],
    "Catamarca": ["San Fernando (CATA)"],
    "Chaco": ["Resistencia (CHAC)"],
    "Chubut": ["Rawson (CHUB)"],
    "Córdoba": ["Capital (CBA)", "Río Cuarto (CBA)"],
    "Corrientes": ["Capital (CORR)"],
    "Entre Ríos": ["Paraná (ER)"],
    "Formosa": ["Capital (FORM)"],
    "Jujuy": ["San Salvador (JUJ)"],
    "La Pampa": ["Santa Rosa (LP)"],
    "La Rioja": ["La Rioja (LR)"],
    "Mendoza": ["Capital (MDZA)", "San Rafael (MDZA)"],
    "Misiones": ["Posadas (MIS)"],
    "Neuquén": ["Capital (NQN)"],
    "Río Negro": ["Capital (RN)"],
    "Salta": ["Capital (SAL)"],
    "San Juan": ["Ciudad (SJ)", "Veladero (SJ)"],
    "San Luis": ["Capital (SL)"],
    "Santa Cruz": ["Rio Gallegos (SC)"],
    "Santa Fe": ["Rosario (SF)", "Santa Fe (SF)"],
    "Santiago del Estero": ["Capital (SGO)"],
    "Tierra del Fuego": ["Ushuaia (TdF)"],
    "Tucumán": ["San Miguel (TUC)"]
};

const MetricItem = ({ label, value, color }) => (
    <div className="hub-metric-row">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span className="hub-dot" style={{ backgroundColor: color }}></span>
            <span className="hub-metric-label">{label}</span>
        </div>
        <span className="hub-metric-value" style={{ color: color }}>{value}</span>
    </div>
);

const SectionHeader = ({ title, icon }) => (
    <div className="hub-section-header">
        <span className="material-icons-round">{icon}</span>
        <h2>{title}</h2>
        <div className="hub-section-line"></div>
    </div>
);

const SuperAdminHub = () => {
    const [user, setUser] = useState(null);
    const [provincia, setProvincia] = useState('Todas');
    const [delegacion, setDelegacion] = useState('Todas');
    const [loading, setLoading] = useState(false);
    const [stats, setStats] = useState({
        padron: { total: 0, activos: 0, adherentes: 0, jubilados: 0, honorarios: 0, pendientes: 0 },
        comercios: { total: 0, hoy: 0 },
        rankingAfi: [],
        rankingComer: []
    });
    const [auditLogs, setAuditLogs] = useState([]);
    const [activeSection, setActiveSection] = useState('dashboard');

    const fetchData = async () => {
        if (!user) return;
        setLoading(true);
        try {
            const { data: afiliados, error: errA } = await supabase.from('afiliados').select('*');
            if (errA) throw errA;

            const { data: prestadores, error: errP } = await supabase.from('prestadores').select('*');
            if (errP) throw errP;

            const todayStart = new Date();
            todayStart.setHours(0, 0, 0, 0);

            let filteredAfi = afiliados;
            let filteredPre = prestadores;
            if (provincia !== 'Todas') {
                filteredAfi = filteredAfi.filter(a => a.provincia === provincia);
                filteredPre = filteredPre.filter(p => p.provincia === provincia);
            }
            if (delegacion !== 'Todas') {
                filteredAfi = filteredAfi.filter(a => a.delegacion === delegacion);
                filteredPre = filteredPre.filter(p => p.delegacion === delegacion);
            }

            const padronReal = filteredAfi.filter(a => {
                const st = (a.estado || '').toLowerCase();
                return st === 'aprobado' || st === 'vigente' || st === 'vigentes' || st === 'activo';
            });

            const getCountByCat = (catName) => {
                return padronReal.filter(a => {
                    const t = (a.tipo_afiliacion || '').toUpperCase();
                    const c = (a.caracter_afiliado || '').toUpperCase();
                    const n = catName.toUpperCase();
                    return t.includes(n) || c.includes(n) || t.includes(n.slice(0, -1)) || c.includes(n.slice(0, -1));
                }).length;
            };

            setStats({
                padron: {
                    total: padronReal.length,
                    activos: getCountByCat('ACTIVO'),
                    adherentes: getCountByCat('ADHERENTE'),
                    jubilados: getCountByCat('JUBILADO'),
                    honorarios: getCountByCat('HONORARIO'),
                    pendientes: filteredAfi.filter(a => (a.estado || '').toLowerCase().includes('pendiente')).length
                },
                comercios: { total: filteredPre.length, hoy: filteredPre.filter(p => new Date(p.created_at) >= todayStart).length },
                rankingAfi: Object.entries(padronReal.reduce((acc, a) => { acc[a.delegacion || 'Sin Delegación'] = (acc[a.delegacion || 'Sin Delegación'] || 0) + 1; return acc; }, {}))
                    .map(([name, count]) => ({ name, count })).sort((a,b) => b.count-a.count).slice(0, 5),
                rankingComer: Object.entries(filteredPre.reduce((acc, p) => { acc[p.delegacion || 'Sin Delegación'] = (acc[p.delegacion || 'Sin Delegación'] || 0) + 1; return acc; }, {}))
                    .map(([name, count]) => ({ name, count })).sort((a,b) => b.count-a.count).slice(0, 5)
            });

            const { data: logs } = await supabase.from('audit_logs').select('*').order('created_at', { ascending: false }).limit(10);
            if (logs) setAuditLogs(logs.map(l => ({
                time: new Date(l.created_at).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' }),
                user: l.user_name || 'Sistema',
                action: l.action,
                destructive: l.is_destructive
            })));
        } catch (e) {
            console.error("Error Hub:", e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        supabase.auth.getSession().then(({ data: { session } }) => {
            if (session) {
                supabase.from('usuarios_admin').select('*').eq('correo', session.user.email).single()
                    .then(({ data }) => { if (data) setUser(data); });
            }
        });
    }, []);

    useEffect(() => {
        if (user) fetchData();
    }, [user, provincia, delegacion]);

    const handleLogout = async () => {
        setLoading(true);
        await supabase.auth.signOut();
        localStorage.clear();
        setUser(null);
        setLoading(false);
    };

    if (!user) return <LoginHub onLoginSuccess={(u) => setUser(u)} />;

    return (
        <div className="hub-layout">
            <aside className="hub-sidebar-left">
                <div className="sb-logo-section">
                    <SupasoLogoSpinner size="small" />
                    <div className="sb-brand">
                        <span className="brand-supaso">SUPASO</span>
                        <span className="brand-admin">HUB</span>
                    </div>
                </div>
                
                <nav className="sb-nav">
                    <button className={`nav-item ${activeSection === 'dashboard' ? 'active' : ''}`} onClick={() => setActiveSection('dashboard')}>
                        <span className="material-icons-round">dashboard</span>
                        <span>Global Hub</span>
                    </button>
                    
                    <div className="sb-divider">ACCESO RÁPIDO</div>

                    <a href="https://app-supaso.vercel.app/admin.html" target="_blank" rel="noopener noreferrer" className="nav-item-link">
                        <span className="material-icons-round">admin_panel_settings</span>
                        <span>Panel Admin Principal</span>
                        <span className="material-icons-round" style={{ fontSize: '14px', marginLeft: 'auto' }}>open_in_new</span>
                    </a>

                    <button className="nav-item">
                        <span className="material-icons-round">analytics</span>
                        <span>Reportes Avanzados</span>
                    </button>
                </nav>

                <div className="sb-footer">
                    <div className="user-info">
                        <div className="user-avatar">{user.nombre[0]}</div>
                        <div className="user-text">
                            <p>{user.nombre} {user.apellido}</p>
                            <span>SUPERADMIN</span>
                        </div>
                    </div>
                    <button className="btn-logout" onClick={handleLogout}>
                        <span className="material-icons-round">logout</span>
                        Cerrar Sesión
                    </button>
                </div>
            </aside>

            <main className="hub-main-content">
                <header className="hub-topbar">
                    <div className="topbar-left">
                        <h1>PANEL DE CONTROL</h1>
                        <p>Supervisión Global de Red</p>
                    </div>
                    <div className="topbar-right">
                        <div className="topbar-filter">
                            <select className="elegant-select" value={provincia} onChange={(e) => { setProvincia(e.target.value); setDelegacion('Todas'); }}>
                                <option value="Todas">TODAS LAS PROVINCIAS</option>
                                {Object.keys(DELEGACIONES_MAP).map(p => <option key={p} value={p}>{p}</option>)}
                            </select>
                            <select className="elegant-select" value={delegacion} onChange={(e) => setDelegacion(e.target.value)} disabled={provincia === 'Todas'}>
                                <option value="Todas">TODAS LAS DELEGACIONES</option>
                                {(DELEGACIONES_MAP[provincia] || []).map(d => <option key={d} value={d}>{d}</option>)}
                            </select>
                        </div>
                        <button className="btn-refresh" onClick={fetchData} title="Actualizar Datos">
                            <span className="material-icons-round">refresh</span>
                        </button>
                    </div>
                </header>

                <div className="hub-content-scroll">
                    <section className="hub-kpi-row">
                        <div className="hub-card hub-card-accent">
                            <div className="kpi-label">PADRÓN TOTAL</div>
                            <div className="kpi-value">{loading ? '...' : stats.padron.total}</div>
                            <div className="kpi-sub">Vigentes en sistema</div>
                        </div>
                        <div className="hub-card">
                            <div className="kpi-label">AFILIACIÓN ACTIVA</div>
                            <div className="kpi-list">
                                <MetricItem label="Titulares Activos" value={stats.padron.activos} color="#3B82F6" />
                                <MetricItem label="Adherentes" value={stats.padron.adherentes} color="#22C55E" />
                            </div>
                        </div>
                        <div className="hub-card">
                            <div className="kpi-label">AFILIACIÓN ESPECIAL</div>
                            <div className="kpi-list">
                                <MetricItem label="Jubilados" value={stats.padron.jubilados} color="#F59E0B" />
                                <MetricItem label="Honorarios" value={stats.padron.honorarios} color="#EF4444" />
                            </div>
                        </div>
                        <div className="hub-card bg-pulse">
                            <div className="kpi-label">PENDIENTES</div>
                            <div className="kpi-value warning">{loading ? '...' : stats.padron.pendientes}</div>
                            <div className="kpi-sub">Aguardando auditoría</div>
                        </div>
                    </section>

                    <section className="hub-commerce-section">
                        <SectionHeader title="Gestión de Comercios" icon="storefront" />
                        <div className="hub-kpi-row">
                            <div className="hub-card-mini">
                                <span className="mini-label">TOTAL PUNTOS</span>
                                <span className="mini-value">{stats.comercios.total}</span>
                            </div>
                            <div className="hub-card-mini">
                                <span className="mini-label">REGISTROS HOY</span>
                                <span className="mini-value success">+{stats.comercios.today}</span>
                            </div>
                            <div className="hub-card-link" onClick={() => window.open('https://app-supaso.vercel.app/admin.html', '_blank')}>
                                <span>Ver detalles en Admin Panal</span>
                                <span className="material-icons-round">arrow_forward</span>
                            </div>
                        </div>
                    </section>

                    <div className="hub-rankings-row">
                        <div className="ranking-card">
                            <h3><span className="material-icons-round">trending_up</span> TOP DELEGACIONES (AFILIADOS)</h3>
                            <ul>
                                {stats.rankingAfi.map((r, i) => (
                                    <li key={i}>
                                        <span className="rank-pos">{i+1}</span>
                                        <span className="rank-name">{r.name}</span>
                                        <span className="rank-val">{r.count}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="ranking-card">
                            <h3><span className="material-icons-round">stars</span> TOP DELEGACIONES (COMERCIOS)</h3>
                            <ul>
                                {stats.rankingComer.map((r, i) => (
                                    <li key={i}>
                                        <span className="rank-pos">{i+1}</span>
                                        <span className="rank-name">{r.name}</span>
                                        <span className="rank-val success">{r.count}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </main>

            <aside className="hub-sidebar-right">
                <div className="sidebar-header">
                    <span className="material-icons-round">sensors</span>
                    <h3>ACTIVITY FEED</h3>
                </div>
                <div className="audit-feed">
                    {auditLogs.map((log, i) => (
                        <div key={i} className={`audit-log ${log.destructive ? 'danger' : ''}`}>
                            <div className="log-header">
                                <span className="log-time">{log.time}</span>
                                <span className="log-user">{log.user}</span>
                            </div>
                            <div className="log-action">{log.action}</div>
                        </div>
                    ))}
                </div>
                <div className="sidebar-footer">
                    <div className="version-info">
                        v1.1 · SUPASO HUB · SUPERADMIN
                    </div>
                </div>
            </aside>
        </div>
    );
};

export default SuperAdminHub;
